import React, { useEffect, useMemo} from 'react';

import styles from './resources.module.scss';
import aboutStyles from '../About/styles.module.scss';
import { useSearchParams } from 'react-router-dom';
import { useRedirectAndScroll } from 'hooks';
import { ResourceItem } from './components/ResourceItem';
import { useTranslates } from 'providers/translates';

import { Language } from 'providers/translates/enums';

import { resourcesItemMemorial, resourcesItemMemorialFR, resourcesItemVac, resourcesItemVacFR } from "../Resources/resources";
import { isVac } from 'utilities/is-vac';
import { Helmet } from 'react-helmet-async';

const discussionForumsLink = 'https://www.virtualhospice.ca/en_US/Main+Site+Navigation/Home/Support/Support/Discussion+Forums.aspx';
const askProfessional = 'https://www.virtualhospice.ca/en_US/Main+Site+Navigation/Home/Support/Support/Ask+a+Professional.aspx';
const localMentalHealth = 'https://www.canada.ca/en/public-health/services/mental-health-services/mental-health-get-help.html';
const wwwNumbersCa = 'https://988.ca';

const translationsHelmet = {
  descriptionEN: "Explore a listing of resources for Veterans and families. MyGrief.ca, KidsGrief.ca, resources for First Nations, Inuit, and Métis Peoples, and mental health and crisis resources.",
  descriptionFR: 'Découvrez des ressources pour les vétéranes et vétérans et leurs familles. MonDeuil.ca, DeuilDesEnfants.ca, ressources pour les Premières Nations, les Inuits et les Métis, ressources en santé mentale et en cas de crise.',
  titleEN: 'Saluting Service | Resources',
  titleFR: 'Saluons leur service | Ressources'
} as const;


const Resources: React.FC = () => {
  const { translates: t, selectedLang } = useTranslates();  
  const [searchParams] = useSearchParams();
  const { onlyScroll } = useRedirectAndScroll({});
  useEffect(() => {
    const scrollTo = searchParams.get('visible');
    if (scrollTo) {
      onlyScroll(scrollTo);
    }
  }, []);
  const resourcesItems = useMemo(() => {
    if (!isVac()) {
      return selectedLang === Language.FRENCH ? resourcesItemMemorialFR : resourcesItemMemorial;
    }
    return selectedLang === Language.FRENCH ? resourcesItemVacFR : resourcesItemVac;
  }, [selectedLang]);

  return (
    <>
    {isVac() && (
      <Helmet>
        <meta name="description" content={selectedLang === Language.FRENCH ? translationsHelmet?.descriptionFR : translationsHelmet?.descriptionEN} />
        <title>{selectedLang === Language.FRENCH ? translationsHelmet?.titleFR : translationsHelmet?.titleEN}</title>
      </Helmet>
    )}
      <section
        className="center resources content"
      >
        <h1 className={aboutStyles.titleH1}>{t?.resources?.title ?? 'Resources'}</h1>
        {!isVac() && (
          <p className='colorNavy'>
            {t?.resources?.description ?? (
              <>
                Everyone’s grief experience is unique, and creating a Healing Quilt may evoke strong
                emotions. You may wish to take a break (you can save your progress and return later), or
                speak with a trusted family member, friend, colleague, or community member. The resources
                listed below may also be of support as you navigate your grief.
              </>
            )}
          </p>
        )}
        
        {!isVac() && (
          <>
          <div className={styles.line} />
            <h2 id='grief-support' className={aboutStyles.titleH2}>{t?.resources?.griefSupport?.title ?? 'Grief support'}</h2>
            {/* <p className='colorNavy'>
              {t?.resources?.griefSupport?.firstParagraph ?? ' You are not alone. Canadian Virtual Hospice provides online '}{''}
              <a className={styles.http_link} href={discussionForumsLink} target={'_blank'}>
                {t?.resources?.griefSupport?.firstLink ?? ' Discussion Forums '}{''}
              </a>
              {t?.resources?.griefSupport?.firstParagraph ?? (
                <>
                  where you can connect with others who are living with loss and grieving. You can share, you
                  can vent, and you will find understanding and support. Our community has been described as
                  “a hug by a warm blanket.”
                </>
              )}
            </p>          
            <p className='colorNavy'>
              {t?.resources?.griefSupport?.thirdParagraph ?? (
                <>
                  You can also connect with a health professional by selecting our
                </>
              )}
              <a className={styles.http_link} href={askProfessional} target={'_blank'}>
                {t?.resources?.griefSupport?.secondLink ?? ' Ask a Professional '}{''}
              </a>
              {t?.resources?.griefSupport?.secondParagraph ?? (
                <>
                  feature to pose a confidential question. A member of the Canadian Virtual Hospice
                  interdisciplinary clinical team will respond within three business days (not including
                  Canadian statutory holidays).
                </>
              )}
            </p> */}
          </>
        )}
        <div className={styles.line} />

        {resourcesItems.map((item, idx) => (
          <>
            <ResourceItem
              key={idx}
              title={item.title}
              imageUrl={item.imageUrl}
              siteUrl={item.siteUrl}
            >{item.text}</ResourceItem>
            <div className={styles.line} />
          </>
        ))}
        {!isVac() && (
          <>
          <h2 className={aboutStyles.titleH2}>{t?.resources?.nationsSection?.title ?? 'For First Nations, Inuit, and Métis Peoples'}</h2>
          <h3 
            className={aboutStyles.titleH3} 
            style={{margin: '30px 0'}}
          >{t?.resources?.nationsSection?.firstSubtitle ?? 'Hope for Wellness Help Line'}</h3>
          <ul className='colorNavy'>
            {t?.resources?.nationsListItems ? (
              t?.resources?.nationsListItems.map((item, index) => {
                if (index === 0) {
                  return (
                    <li className={styles.list_item} key={item.id}>
                      <span className={styles.list_dot}></span>
                      <span>
                        {item.text}&nbsp;
                        <a
                          className={styles.http_link}
                          href="https://www.hopeforwellness.ca"
                          target="_blank"
                          rel="noreferrer"
                        >
                          {t?.resources?.nationsSection?.link}
                        </a>
                        .
                      </span>
                    </li>
                  );
                }

                return (
                  <li className={styles.list_item} key={item.id}>
                    <span className={styles.list_dot}></span>{item.text}
                  </li>
                );
              })
            ) : (
              <>
                <li className={styles.list_item}>
                  <span className={styles.list_dot}></span>
                  <span>
                    Call 1-855-242-3310 (toll-free) or connect to the online&nbsp;
                    <a
                      className={styles.http_link}
                      href="https://www.hopeforwellness.ca"
                      target="_blank"
                      rel="noreferrer"
                    >
                      Hope for Wellness chat
                    </a>
                    .
                  </span>
                </li>
                <li className={styles.list_item}>
                  <span className={styles.list_dot}></span>Available 24 hours a day, 7 days a week to First
                  Nations, Inuit, and Métis Peoples seeking emotional support, crisis intervention, or
                  referrals to community-based services.
                </li>
                <li className={styles.list_item}>
                  <span className={styles.list_dot}></span>Support is available in English and French and,
                  by request, in Cree, Ojibway, and Inuktitut.
                </li>
              </>
            )}
          </ul>
          <h3 
            className={aboutStyles.titleH3} 
            style={{margin: '30px 0'}}
          >{t?.resources?.nationsSection?.secondSubtitle ?? 'The National Indian Residential School Crisis Line'}</h3>
          <p className="my-20 colorNavy">
            {t?.resources?.nationsSection?.firstParagraph ?? (
              <>
                Crisis support is available to former Indian Residential School students and their families
                24 hours a day, 7 days a week at 1-866-925-4419 (toll-free).
              </>
            )}
          </p>
          <h3 
            className={aboutStyles.titleH3} 
            style={{margin: '30px 0'}}
          >
            {t?.resources?.nationsSection?.thirdSubtitle ?? 'Missing and Murdered Indigenous Women and Girls Crisis Line'}
          </h3>
          <p className="my-20 colorNavy">
            {t?.resources?.nationsSection?.secondParagraph ?? (
              <>
                Crisis support is available to individuals impacted by the issue of missing and murdered
                Indigenous women, girls, and 2SLGBTQQIA+ people 24 hours a day, 7 days a week at
                1-844-413-6649 (toll-free).
              </>
            )}
          </p>

          <div className={styles.line} />

          <h2 className={aboutStyles.titleH2}>{t?.resources?.mentalHealthSection?.title ?? 'Mental health and crisis resources'}</h2>
          <h3 
            className={aboutStyles.titleH3} 
            style={{margin: '30px 0'}}
          >{t?.resources?.mentalHealthSection?.firstSubtitle ?? 'Provincial and Territorial Mental Health Resources'}</h3>
          <p className='cmy-20 colorNavy'>
            {t?.resources?.mentalHealthSection?.firstParagraph ?? 'Find'}
            <a 
              className={styles.http_link} 
              href={localMentalHealth} 
              target={'_blank'}
            >
              {t?.resources?.mentalHealthSection?.firstLink ?? ' local mental health'}{''}
            </a>
            {t?.resources?.mentalHealthSection?.secondParagraph ?? 'resources and supports within your province or territory.'}{''}
          </p>

          <h3 
            className={aboutStyles.titleH3} 
            style={{margin: '30px 0'}}
          >{t?.resources?.mentalHealthSection?.secondSubtitle ?? '9-8-8 Suicide Crisis Helpline'}</h3>
          <ul className='colorNavy'>
            {t?.resources?.mentalHealthListItems ? (
              t?.resources?.mentalHealthListItems.map((item, index) => {
                if (index === 0) {
                  return (
                    <li className={styles.list_item} key={item.id}>
                      <span className={styles.list_dot}></span>
                      <span>
                        {item.text}
                        <a
                          className={styles.http_link}
                          href={wwwNumbersCa}
                          target={'_blank'}
                          style={{
                            marginLeft: '8px',
                          }}
                        >
                          {t?.resources?.mentalHealthSection?.secondLink}
                        </a>
                        .
                      </span>
                    </li>
                  );
                }

                return (
                  <li className={styles.list_item} key={item.id}>
                    <span className={styles.list_dot}></span>{item.text}
                  </li>
                );
              })
            ) : (
              <>
                <li className={styles.list_item}>
                  <span className={styles.list_dot}></span>
                  <span>
                    Call or text 9-8-8, 24/7 or visit
                    <a
                      className={styles.http_link}
                      href={wwwNumbersCa}
                      target={'_blank'}
                      style={{
                        marginLeft: '8px',
                      }}
                    >
                      988.ca
                    </a>
                    .
                  </span>
                </li>
                <li className={styles.list_item}>
                  <span className={styles.list_dot}></span>Support for immediate crisis or suicide-related
                  concerns.
                </li>
                <li className={styles.list_item}>
                  <span className={styles.list_dot}></span>Culturally appropriate and trauma-informed
                  support available to anyone in Canada in English and French.
                </li>
              </>
            )}
          </ul>
          </>
        )}
      </section>
    </>
  );
};

export default Resources;
